import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './empStyle.css';
import config from '../../config.json';
import EmpLabel from './EmpLabel';

const apiUrl = config.apiUrl;
const getStatusBadge = (status) => {
    let variant;
    switch (status) {
        case 'Rejected':
            variant = 'danger';
            break;
        case 'Pending':
            variant = 'primary';
            break;
        case 'Approved':
            variant = 'success';
            break;
        default:
            variant = 'secondary';
    }
    return <Badge bg={variant}>{status}</Badge>;
};


const EmpDetails = () => {
    const [employeeDetails, setEmployeeDetails] = useState({
        IDNumber: '',
        LastName: '',
        FirstName: '',
        MiddleInitial: '',
        Position: '',
        User: [],
        Status: '',
        CreatedBy: '',
        CreatedDate: '',
        UpdatedBy: '',
        UpdatedDate: '',
    });

    const { employeeId } = useParams();

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const navigate = useNavigate();
    const [itemsPerPage] = useState(10);
    const [overtimeData, setOvertimeData] = useState([]);
    const [leaveData, setLeaveData] = useState([]);
    const [undertimeData, setUndertimeData] = useState([]);
    const [timeentryData, setTimeEntryData] = useState([]);
    const [overtimeCurrentPage, setOvertimeCurrentPage] = useState(1);
    const [leaveCurrentPage, setLeaveCurrentPage] = useState(1);
    const [undertimeCurrentPage, setUndertimeCurrentPage] = useState(1);
    const [timeEntryCurrentPage, setTimeEntryCurrentPage] = useState(1);
    const [overtimeTotalPages, setOvertimeTotalPages] = useState(0);
    const [leaveTotalPages, setLeaveTotalPages] = useState(0);
    const [undertimeTotalPages, setUndertimeTotalPages] = useState(0);
    const [timeEntryTotalPages, setTimeEntryTotalPages] = useState(0);
    const [isOvertimeLoading, setIsOvertimeLoading] = useState(false);
    const [isLeaveLoading, setIsLeaveLoading] = useState(false);
    const [isUndertimeLoading, setIsUndertimeLoading] = useState(false);
    const [isTimeEntryLoading, setIsTimeEntryLoading] = useState(false);
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const [activeTab, setActiveTab] = useState('Overtime');

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const buildStatusQueryString = () => [0, 1, 2].map((status) => `statuses=${status}`).join('&');

    useEffect(() => {
        if (employeeId) {
            fetchEmployeeData(employeeId);
        }
    }, [employeeId]);

    useEffect(() => {
        if (employeeId) {
            fetchOvertimeData(employeeId, overtimeCurrentPage);
        }
    }, [employeeId, overtimeCurrentPage]);

    useEffect(() => {
        if (employeeId) {
            fetchLeaveData(employeeId, leaveCurrentPage);
        }
    }, [employeeId, leaveCurrentPage]);

    useEffect(() => {
        if (employeeId) {
            fetchUndertimeData(employeeId, undertimeCurrentPage);
        }
    }, [employeeId, undertimeCurrentPage]);

    useEffect(() => {
        if (employeeId) {
            fetchTimeEntryData(employeeId, timeEntryCurrentPage);
        }
    }, [employeeId, timeEntryCurrentPage]);

    const fetchOvertimeData = async (employeeId, pageNumber = overtimeCurrentPage) => {
        setIsOvertimeLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/ReportView/Overtime?${buildStatusQueryString()}&EmployeeID=${employeeId}&pageNumber=${pageNumber}&pageSize=${itemsPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    // Handle other error cases
                    setErrorMessage('Failed to fetch data');
                    setSuccessMessage('');
                    return;
                }
            }

            setOvertimeData(data.Data || []);
            setOvertimeTotalPages(data.TotalCount ? Math.ceil(data.TotalCount / itemsPerPage) : 0);
            setOvertimeCurrentPage(data.CurrentPage || pageNumber);

        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        } finally {
            setIsOvertimeLoading(false);
        }
    };

    const fetchLeaveData = async (employeeId, pageNumber = leaveCurrentPage) => {
        setIsLeaveLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/ReportView/Leave?${buildStatusQueryString()}&EmployeeID=${employeeId}&pageNumber=${pageNumber}&pageSize=${itemsPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    // Handle other error cases

                    return;
                }
            }

            setLeaveData(data.Data || []);
            setLeaveTotalPages(data.TotalCount ? Math.ceil(data.TotalCount / itemsPerPage) : 0);
            setLeaveCurrentPage(data.CurrentPage || pageNumber);
        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        } finally {
            setIsLeaveLoading(false);
        }
    };

    const fetchUndertimeData = async (employeeId, pageNumber = undertimeCurrentPage) => {
        setIsUndertimeLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/ReportView/Undertime?${buildStatusQueryString()}&EmployeeID=${employeeId}&pageNumber=${pageNumber}&pageSize=${itemsPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    setErrorMessage('Error fetching data');
                    setSuccessMessage('');
                    return;
                }

            }

            setUndertimeData(data.Data || []);
            setUndertimeTotalPages(data.TotalCount ? Math.ceil(data.TotalCount / itemsPerPage) : 0);
            setUndertimeCurrentPage(data.CurrentPage || pageNumber);
        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        } finally {
            setIsUndertimeLoading(false);
        }
    };

    const fetchTimeEntryData = async (employeeId, pageNumber = timeEntryCurrentPage) => {
        setIsTimeEntryLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/ReportView/TimeEntry?${buildStatusQueryString()}&employeeID=${employeeId}&pageNumber=${pageNumber}&pageSize=${itemsPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    setErrorMessage('Error fetching data');
                    setSuccessMessage('');
                    return;
                }

            }

            setTimeEntryData(data.Data || []);
            setTimeEntryTotalPages(data.TotalCount ? Math.ceil(data.TotalCount / itemsPerPage) : 0);
            setTimeEntryCurrentPage(data.CurrentPage || pageNumber);
        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        } finally {
            setIsTimeEntryLoading(false);
        }
    };

    const fetchEmployeeData = async (employeeId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/Employee/${employeeId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    setErrorMessage('Failed to fetch data');
                    setSuccessMessage('');
                    return;
                }
            }

            setEmployeeDetails({
                IDNumber: data.Details.IdNumber,
                LastName: data.Details.LastName,
                FirstName: data.Details.FirstName,
                MiddleInitial: data.Details.MiddleInitial,
                Position: data.Details.Position,
                User: data.Details.User || [],
                Status: data.Details.Status,
                CreatedBy: data.Details.CreatedBy,
                CreatedDate: data.Details.CreatedDate,
                UpdatedBy: data.Details.UpdatedBy,
                UpdatedDate: data.Details.UpdatedDate,
            });

        } catch (error) {
            setErrorMessage('Error fetching employee data');
            setSuccessMessage('');
        }
    };

    const { IDNumber, LastName, FirstName, MiddleInitial, Position, User, Status, CreatedBy, CreatedDate, UpdatedBy, UpdatedDate } = employeeDetails;

    const renderPagination = (currentPage, totalPages, setCurrentPage, ariaLabel) => {
        const pageGroupSize = 5;
        const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
        const startPage = currentGroup * pageGroupSize + 1;
        const endPage = Math.min(startPage + pageGroupSize - 1, totalPages === 0 ? 1 : totalPages);
        const visiblePages = totalPages > 0
            ? Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index)
            : [];

        return (
            <nav aria-label={ariaLabel}>
                <ul className="pagination mt-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(1)} aria-label="First">
                            <span aria-hidden="true">{"<<"}</span>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous">
                            <span aria-hidden="true">{"<"}</span>
                        </button>
                    </li>
                    {visiblePages.map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(page)}>
                                {page}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next">
                            <span aria-hidden="true">{">"}</span>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(totalPages)} aria-label="Last">
                            <span aria-hidden="true">{">>"}</span>
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div>
            {(userAccess.hasAdmin || userAccess.hasStandard) ? (
                <>
                    <h3>View Employee</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <div className="row mt-4">
                        <div className="col-md-6">
                            <EmpLabel label="ID Number" value={IDNumber} />
                            <EmpLabel label="First Name" value={FirstName} />
                            <EmpLabel label="Middle Name" value={MiddleInitial} />
                            <EmpLabel label="Last Name" value={LastName} />
                            <EmpLabel label="Position" value={Position} />
                            <EmpLabel label="User" value={User.Email} />

                            <div className="mb-2">
                                <Link to="/employee-list" className="btn btn-secondary">Back</Link>
                                {userAccess.hasAdmin && (
                                    <Link to={`/employee-update/${employeeId}`}
                                        className={`btn btn-primary ms-2 ${Status === 'Inactive' ? 'disabled' : ''}`}>Edit
                                    </Link>
                                )}
                            </div>

                        </div>
                        <div className="col-md-6">
                            <EmpLabel label="Status" value={Status} />
                            <EmpLabel label="Created By" value={CreatedBy} />
                            <EmpLabel label="Created Date" value={CreatedDate} />
                            <EmpLabel label="Updated By" value={UpdatedBy} />
                            <EmpLabel label="Updated Date" value={UpdatedDate} />
                        </div>
                    </div>

                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <div
                                className={`nav-link ${activeTab === 'Overtime' ? 'active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTabClick('Overtime')}
                            >
                                Overtime
                            </div>
                        </li>
                        <li className="nav-item">
                            <div
                                className={`nav-link ${activeTab === 'Leave' ? 'active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTabClick('Leave')}
                            >
                                Leave
                            </div>
                        </li>
                        <li className="nav-item">
                            <div
                                className={`nav-link ${activeTab === 'Undertime' ? 'active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTabClick('Undertime')}
                            >
                                Undertime
                            </div>
                        </li>
                        <li className="nav-item">
                            <div
                                className={`nav-link ${activeTab === 'TimeEntry' ? 'active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTabClick('TimeEntry')}
                            >
                                Time Entries
                            </div>
                        </li>
                    </ul>

                    <div className="tab-content mt-3">
                        <div
                            className={`tab-pane ${activeTab === 'Overtime' ? 'active show' : ''}`}
                        >
                            <table className='custom-table'>
                                <thead>
                                    <tr>
                                        <th className='border-end-0'>Transaction ID</th>
                                        <th className='border-start-0 border-end-0'>Transaction Date</th>
                                        <th className='border-start-0 border-end-0'>Employee</th>
                                        <th className='border-start-0 border-end-0'>Date Start</th>
                                        <th className='border-start-0 border-end-0'>Date End</th>
                                        <th className='border-start-0 border-end-0'>Duration</th>
                                        <th className='border-start-0'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isOvertimeLoading ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border me-2" aria-hidden="true"></div>
                                                    <span role="status"> Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        overtimeData && overtimeData.length > 0 ? (
                                            overtimeData.map((item) => (
                                                <React.Fragment key={item.ID}>
                                                    <tr>
                                                        <td className='border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                        <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                        <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="9" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                            <strong>Remarks:</strong> {item.Remarks}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">No Data</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                            {renderPagination(overtimeCurrentPage, overtimeTotalPages, setOvertimeCurrentPage, 'Overtime page navigation')}
                        </div>
                        <div
                            className={`tab-pane ${activeTab === 'Leave' ? 'active show' : ''}`}
                        >
                            <table className='custom-table'>
                                <thead>
                                    <tr>
                                        <th className='border-end-0'>Transaction ID</th>
                                        <th className='border-start-0 border-end-0'>Transaction Date</th>
                                        <th className='border-start-0 border-end-0'>Employee</th>
                                        <th className='border-start-0 border-end-0'>Leave Type</th>
                                        <th className='border-start-0 border-end-0'>Date Start</th>
                                        <th className='border-start-0 border-end-0'>Date End</th>
                                        <th className='border-start-0 border-end-0'>Duration</th>
                                        <th className='border-start-0'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLeaveLoading ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border me-2" aria-hidden="true"></div>
                                                    <span role="status"> Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        leaveData && leaveData.length > 0 ? (
                                            leaveData.map((item) => (
                                                <React.Fragment key={item.ID}>
                                                    <tr>
                                                        <td className='border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.LeaveType}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                        <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                        <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="9" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                            <strong>Remarks:</strong> {item.Remarks}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">No Data</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                            {renderPagination(leaveCurrentPage, leaveTotalPages, setLeaveCurrentPage, 'Leave page navigation')}
                        </div>
                        <div
                            className={`tab-pane ${activeTab === 'Undertime' ? 'active show' : ''}`}
                        >
                            <table className='custom-table'>
                                <thead>
                                    <tr>
                                        <th className='border-end-0'>Transaction ID</th>
                                        <th className='border-start-0 border-end-0'>Transaction Date</th>
                                        <th className='border-start-0 border-end-0'>Employee</th>
                                        <th className='border-start-0 border-end-0'>Date Start</th>
                                        <th className='border-start-0 border-end-0'>Date End</th>
                                        <th className='border-start-0 border-end-0'>Duration</th>
                                        <th className='border-start-0'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isUndertimeLoading ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border me-2" aria-hidden="true"></div>
                                                    <span role="status"> Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        undertimeData && undertimeData.length > 0 ? (
                                            undertimeData.map((item) => (
                                                <React.Fragment key={item.ID}>
                                                    <tr>
                                                        <td className='border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                        <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                        <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="9" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                            <strong>Remarks:</strong> {item.Remarks}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">No Data</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                            {renderPagination(undertimeCurrentPage, undertimeTotalPages, setUndertimeCurrentPage, 'Undertime page navigation')}
                        </div>
                        <div
                            className={`tab-pane ${activeTab === 'TimeEntry' ? 'active show' : ''}`}
                        >
                            <table className='custom-table'>
                                <thead>
                                    <tr>
                                        <th className='border-end-0'>Transaction Date</th>
                                        <th className='border-start-0 border-end-0'>Employee</th>
                                        <th className='border-start-0 border-end-0'>Start Date</th>
                                        <th className='border-start-0 border-end-0'>End Date</th>
                                        <th className='border-start-0 border-end-0'>Duration</th>
                                        <th className='border-start-0 border-end-0'>Entry Type</th>
                                        <th className='border-start-0'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isTimeEntryLoading ? (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border me-2" aria-hidden="true"></div>
                                                    <span role="status"> Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        timeentryData && timeentryData.length > 0 ? (
                                            timeentryData.map((item) => (
                                                <React.Fragment key={item.ID}>
                                                    <tr>
                                                        <td className='border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.StartDate}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.EndDate}</td>
                                                        <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                        <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.EntryTypeID}</td>
                                                        <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="9" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                            <strong>Remarks:</strong> {item.Remarks}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">No Data</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                            {renderPagination(timeEntryCurrentPage, timeEntryTotalPages, setTimeEntryCurrentPage, 'Time Entry page navigation')}
                        </div>
                    </div>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    )
}

export default EmpDetails

